import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthHeader from "../components/AuthHeader";
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";

function HomeworkWordsPage({ API_BASE }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [homeworkList, setHomeworkList] = useState([]);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [wordCards, setWordCards] = useState([]);
  const [quizMode, setQuizMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [inputText, setInputText] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);

  const qwertyKeys = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm']
  ];

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/homework_words' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    fetchHomework();
  }, []);

  const fetchHomework = async () => {
    try {
      const response = await fetch(`${API_BASE}/homework`);
      const data = await response.json();
      setHomeworkList(data);
    } catch (error) {
      console.error("Error fetching homework:", error);
    }
  };

  const fetchWordCards = async (homeworkId) => {
    try {
      const response = await fetch(`${API_BASE}/homework/${homeworkId}/cards`);
      const data = await response.json();
      setWordCards(data);
      setSelectedHomework(homeworkId);
    } catch (error) {
      console.error("Error fetching word cards:", error);
    }
  };

  const startQuiz = () => {
    setQuizMode(true);
    setCurrentQuestion(0);
    setScore(0);
    setInputText("");
    setShowResult(false);
    setQuizFinished(false);
  };

  const handleKeyPress = (key) => {
    if (showResult) return;
    setInputText(prev => prev + key);
  };

  const handleDelete = () => {
    if (showResult) return;
    setInputText(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    if (showResult) return;
    setInputText("");
  };

  const handleSubmit = () => {
    if (showResult || inputText.trim() === "") return;
    
    const currentCard = wordCards[currentQuestion];
    const correctAnswer = currentCard.choices.find(choice => choice.choice_type === 'correct');
    const isCorrect = inputText.trim().toLowerCase() === correctAnswer.choice_word.toLowerCase();
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    setShowResult(true);
    
    setTimeout(() => {
      if (currentQuestion + 1 >= wordCards.length) {
        setQuizFinished(true);
      } else {
        setCurrentQuestion(currentQuestion + 1);
        setInputText("");
        setShowResult(false);
      }
    }, 2000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const handleHomeworkClick = (homework) => {
    fetchWordCards(homework.id);
  };

  const handleBackClick = () => {
    setSelectedHomework(null);
    setWordCards([]);
    setQuizMode(false);
    setQuizFinished(false);
    setInputText("");
  };

  // ログイン中のみ表示
  if (!isAuthenticated) {
    return null; // リダイレクト中は何も表示しない
  }

  if (selectedHomework) {
    if (quizFinished) {
      return (
        <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{ fontWeight: 'bold', marginBottom: 4 }}
            >
              🎉 クイズ完了！
            </Typography>
            
            <Card sx={{ maxWidth: 400, margin: '0 auto', padding: 4 }}>
              <CardContent>
                <Typography variant="h4" color="primary" sx={{ marginBottom: 2 }}>
                  {score} / {wordCards.length}
                </Typography>
                <Typography variant="h6" sx={{ marginBottom: 3 }}>
                  正解率: {Math.round((score / wordCards.length) * 100)}%
                </Typography>
                
                <Stack spacing={2}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={startQuiz}
                  >
                    もう一度挑戦
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleBackClick}
                  >
                    宿題一覧に戻る
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Container>
      );
    }
    
    if (quizMode && wordCards.length > 0) {
      const currentCard = wordCards[currentQuestion];
      const correctAnswer = currentCard.choices.find(choice => choice.choice_type === 'correct');
      
      return (
        <Container maxWidth={false} sx={{ paddingTop: 1, paddingBottom: 1, width: '100%', px: 1, height: '640px', overflow: 'hidden' }}>
          <Box sx={{ marginBottom: 1 }}>
            <Button 
              variant="outlined" 
              onClick={handleBackClick}
              sx={{ marginBottom: 1, height: '32px', fontSize: '0.8rem' }}
            >
              ← 戻る
            </Button>
            
            <Typography variant="body1" align="center" sx={{ marginBottom: 1, fontSize: '0.9rem' }}>
              問題 {currentQuestion + 1} / {wordCards.length}
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={(currentQuestion / wordCards.length) * 100}
              sx={{ marginBottom: 1 }}
            />
          </Box>
          
          <Card sx={{ maxWidth: 600, margin: '0 auto', padding: 2, marginBottom: 2 }}>
            <CardContent sx={{ padding: 1 }}>
              <Typography 
                variant="h5" 
                component="h2" 
                align="center"
                sx={{ 
                  fontWeight: 'bold', 
                  marginBottom: 0.5,
                  color: 'primary.main'
                }}
              >
                {currentCard.word_jp}
              </Typography>
              
              <Typography 
                variant="body2" 
                align="center"
                color="text.secondary"
                sx={{ marginBottom: 1 }}
              >
                ({currentCard.pos})
              </Typography>
              
              <Box sx={{ textAlign: 'center', marginBottom: 1 }}>
                <Typography 
                  variant="body1" 
                  component="div" 
                  sx={{ 
                    borderBottom: '2px solid #000',
                    paddingBottom: 0.5,
                    minHeight: '1.5rem',
                    fontSize: '1.2rem',
                    fontFamily: 'monospace',
                    textAlign: 'center',
                    width: '100%',
                    margin: '0 auto'
                  }}
                >
                  {inputText || '\u00A0'}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', marginBottom: 1 }}>
                <Button 
                  variant="contained" 
                  onClick={handleSubmit}
                  disabled={inputText.trim() === "" || showResult}
                  sx={{ minWidth: '100px', height: '36px', fontSize: '0.9rem' }}
                >
                  回答
                </Button>
              </Box>
              
              {showResult && (
                <Box sx={{ textAlign: 'center', marginTop: 1 }}>
                  <Typography 
                    variant="body1" 
                    color={inputText.trim().toLowerCase() === correctAnswer.choice_word.toLowerCase() ? 'success.main' : 'error.main'}
                  >
                    {inputText.trim().toLowerCase() === correctAnswer.choice_word.toLowerCase() ? '✅ 正解！' : '❌ 不正解'}
                  </Typography>
                  {inputText.trim().toLowerCase() !== correctAnswer.choice_word.toLowerCase() && (
                    <Typography variant="body2" color="text.secondary">
                      正解: {correctAnswer.choice_word}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1, marginBottom: 1 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleClear}
                disabled={showResult}
                sx={{ 
                  minWidth: '50px', 
                  height: '30px',
                  fontSize: '0.8rem'
                }}
              >
                全削除
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleDelete}
                disabled={showResult}
                sx={{ 
                  minWidth: '50px', 
                  height: '30px',
                  fontSize: '0.8rem'
                }}
              >
                1文字削除
              </Button>
            </Box>

            {qwertyKeys.map((row, rowIndex) => (
              <Box key={rowIndex} sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', width: '100%' }}>
                {row.map((key) => (
                  <Button
                    key={key}
                    variant="outlined"
                    onClick={() => handleKeyPress(key)}
                    disabled={showResult}
                    sx={{
                      width: 'calc(10% - 2px)',
                      minWidth: '24px',
                      height: '32px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      px: 0.2,
                      textTransform: 'none'
                    }}
                  >
                    {key}
                  </Button>
                ))}
              </Box>
            ))}
          </Box>
        </Container>
      );
    }
    
    return (
      <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
        <Box sx={{ marginBottom: 4 }}>
          <Button 
            variant="outlined" 
            onClick={handleBackClick}
            sx={{ marginBottom: 2 }}
          >
            ← 戻る
          </Button>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold' }}
          >
            ⌨️ 単語入力クイズ
          </Typography>
        </Box>
        
        <Card sx={{ maxWidth: 400, margin: '0 auto', padding: 4 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ marginBottom: 3 }}>
              {wordCards.length}問のクイズがあります
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 4 }}>
              日本語の意味に対応する正しい英単語をキーボードで入力してください。
            </Typography>
            
            <Button 
              variant="contained" 
              size="large"
              onClick={startQuiz}
              sx={{ width: '100%' }}
            >
              クイズを開始
            </Button>
          </CardContent>
        </Card>
        
        {wordCards.length === 0 && (
          <Typography variant="body1" align="center" color="text.secondary">
            単語データがありません
          </Typography>
        )}
      </Container>
    );
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom 
        align="center"
        sx={{ fontWeight: 'bold', marginBottom: 4 }}
      >
      </Typography>
      
      <Box sx={{ marginTop: 4 }}>
        <Stack spacing={2}>
          {homeworkList.map((homework) => (
            <Button
              key={homework.id}
              variant="outlined"
              size="large"
              onClick={() => handleHomeworkClick(homework)}
              sx={{
                padding: 2,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textAlign: 'left',
                justifyContent: 'flex-start',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                }
              }}
            >
              {homework.week_num || formatDate(homework.processed_at)}
            </Button>
          ))}
        </Stack>
        
        {homeworkList.length === 0 && (
          <Typography variant="body1" align="center" color="text.secondary">
            宿題データがありません
          </Typography>
        )}
      </Box>
    </Container>
    </>
  );
}

export default HomeworkWordsPage;